// OffenePostenTabelle.tsx — die Arbeitsliste des Moduls Zahlungsverkehr.
//
// Bewusst auf POSITIONS-Ebene statt auf Rechnungsebene: eine Rechnung mit 40
// Positionen kann teils bezahlt, teils offen und teils storniert sein — „wer ist
// am Zug" ist nur je Position beantwortbar.
//
// Standardfilter ist „nicht erledigt". Im Altbestand sind 98,4 % der Rechnungen
// vollständig bezahlt; eine Liste, die zuerst alles zeigt, versteckt die Arbeit
// im Rauschen.
//
// Gebucht wird DIREKT IN DER TABELLE: die vier Schalter (Kunde bezahlt,
// Versicherer bezahlt, KaV, storniert) speichern sofort und patchen die Zeile
// lokal — kein Reload, damit die Scroll-Position erhalten bleibt.

import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { I } from "./kvpIcons";
import { DataTable, Dash, type Column, type SortState } from "./table";
import "./contactRedesign.css";
import { apiFetch } from "../../utils/apiFetch";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { useInfiniteRows } from "../../hooks/useInfiniteRows";
import { formatEuro, formatDate } from "./contractRiskCells";

export type AmZug = "VN am Zug" | "Courte am Zug" | "erledigt" | "storniert" | "KaV";

export type PositionRow = {
  id: number;
  invoiceId: number;
  invoiceNbr: string;
  invoiceDate: string | null;
  dueDate: string | null;
  status: number;
  contactId: number | null;
  kunde: string | null;
  contractId: number | null;
  contractNbr: string | null;
  branch: string | null;
  posNo: number | null;
  periodFrom: string | null;
  periodTo: string | null;
  amount: number | null;
  customerTarget: number | null;
  customerPaid: number | null;
  customerOpen: number | null;
  insurerTarget: number | null;
  insurerPaid: number | null;
  insurerOpen: number | null;
  isCancelled: boolean;
  customerPaysInsurer: boolean;
  remark: string | null;
  amZug: AmZug;
};

type Summen = { beitrag: number; offenKunde: number; offenVersicherer: number };

/** Farbrolle je Zustand — rot = wir warten auf Geld, orange = wir schulden. */
const CHIP_TONE: Record<AmZug, string> = {
  "VN am Zug": "chip-danger",
  "Courte am Zug": "chip-warn",
  erledigt: "chip-ok",
  storniert: "chip-muted",
  KaV: "chip-muted",
};

const CHIP_LABEL: Record<AmZug, string> = {
  "VN am Zug": "VN am Zug",
  "Courte am Zug": "Courté am Zug",
  erledigt: "erledigt",
  storniert: "storniert",
  KaV: "KaV",
};

/** Ableitung wie im Backend — für das optimistische Patchen nach einer Buchung. */
function amZugFor(p: Pick<PositionRow, "isCancelled" | "customerPaysInsurer" | "customerOpen" | "insurerOpen">): AmZug {
  if (p.isCancelled) return "storniert";
  if (p.customerPaysInsurer) return "KaV";
  if ((p.customerOpen ?? 0) > 0) return "VN am Zug";
  if ((p.insurerOpen ?? 0) > 0) return "Courte am Zug";
  return "erledigt";
}

/**
 * Buchungsschalter. Muster A („immer offenes Feld"): ein Klick, sofort
 * gespeichert — das ist der Hauptvorgang, jede Zwischenstufe wäre Reibung.
 */
function BuchungsSchalter({
  checked,
  disabled,
  title,
  onToggle,
}: {
  checked: boolean;
  disabled?: boolean;
  title: string;
  onToggle: (next: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      className="cbx"
      checked={checked}
      disabled={disabled}
      title={title}
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onToggle(e.target.checked)}
    />
  );
}

type Props = {
  /** Auf einen Kontakt einschränken (Kontakt-Reiter „Zahlungsverkehr"). */
  contactId?: number;
  /** Auf einen Vertrag einschränken (Vertrags-Detail). */
  contractId?: number;
  /** Auf eine Rechnung einschränken (Positionen im Rechnungs-Detail). */
  invoiceId?: number;
  /** Ohne Kopfzeile/Suche rendern, wenn eingebettet. */
  kompakt?: boolean;
};

const OffenePostenTabelle: React.FC<Props> = ({ contactId, contractId, invoiceId, kompakt }) => {
  const navigate = useNavigate();

  const [nurOffen, setNurOffen] = useState(!invoiceId);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<SortState | null>(null);
  const [summen, setSummen] = useState<Summen>({ beitrag: 0, offenKunde: 0, offenVersicherer: 0 });
  const [fehler, setFehler] = useState<string | null>(null);

  const fetchPage = useCallback(
    async ({ limit, offset, signal }: { limit: number; offset: number; signal: AbortSignal }) => {
      const params = new URLSearchParams({
        offen: String(nurOffen),
        limit: String(limit),
        offset: String(offset),
      });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (contactId) params.set("contactId", String(contactId));
      if (contractId) params.set("contractId", String(contractId));
      if (invoiceId) params.set("invoiceId", String(invoiceId));
      if (sort?.field) {
        params.set("sort", sort.field);
        params.set("direction", sort.direction === "asc" ? "ASC" : "DESC");
      }

      const res = await apiFetch<PositionRow[]>(`invoice-positions?${params.toString()}`, { signal });
      const headers = res.response?.headers;
      if (headers) {
        setSummen({
          beitrag: Number(headers.get("X-Sum-Beitrag") ?? 0),
          offenKunde: Number(headers.get("X-Sum-Offen-Kunde") ?? 0),
          offenVersicherer: Number(headers.get("X-Sum-Offen-Versicherer") ?? 0),
        });
      }
      return { rows: res.data ?? [], total: res.total ?? null };
    },
    [nurOffen, debouncedSearch, contactId, contractId, invoiceId, sort],
  );

  const { rows, total, hasMore, loading, loadMore, patchByKey } = useInfiniteRows<PositionRow>({
    fetchPage,
    getKey: (r) => r.id,
    deps: [nurOffen, debouncedSearch, contactId, contractId, invoiceId, sort],
  });

  /** Schaltet ein Kennzeichen und patcht die Zeile lokal (kein Reload). */
  const buchen = useCallback(
    async (row: PositionRow, patch: Partial<Record<"customerPaid" | "insurerPaid" | "isCancelled" | "customerPaysInsurer", boolean>>) => {
      setFehler(null);
      try {
        const res = await apiFetch<{ position: PositionRow }>(
          `invoice-positions/${row.id}/flags`,
          { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) },
        );
        const p = res.data?.position;
        if (!p) return;
        patchByKey(row.id, (alt) => {
          const neu = { ...alt, ...p } as PositionRow;
          neu.amZug = amZugFor(neu);
          return neu;
        });
      } catch (e) {
        // Der Server lehnt Doppelbuchungen mit 409 ab (KaV bei storniert usw.).
        setFehler(
          e instanceof Error && e.message
            ? e.message
            : "Buchung nicht möglich — bitte Seite neu laden.",
        );
      }
    },
    [patchByKey],
  );

  const columns = useMemo<Column<PositionRow>[]>(() => {
    const cols: Column<PositionRow>[] = [
      {
        id: "amZug",
        header: "Wer ist dran",
        width: 118,
        cell: (r) => <span className={`chip ${CHIP_TONE[r.amZug]}`}>{CHIP_LABEL[r.amZug]}</span>,
      },
      {
        id: "kunde",
        header: "Kunde",
        sortable: true,
        sortField: "kunde",
        minWidth: 180,
        cell: (r) =>
          r.kunde ? (
            <button
              className="lnk"
              onClick={(e) => {
                e.stopPropagation();
                if (r.contactId) navigate(`/contacts/${r.contactId}`);
              }}
            >
              {r.kunde}
            </button>
          ) : (
            <Dash />
          ),
      },
      {
        id: "vertrag",
        header: "Vertrag",
        sortable: true,
        sortField: "vertrag",
        width: 180,
        cell: (r) =>
          r.contractNbr ? (
            <button
              className="lnk"
              onClick={(e) => {
                e.stopPropagation();
                if (r.contractId) navigate(`/contracts/${r.contractId}`);
              }}
            >
              {r.contractNbr}
              {r.branch ? <span className="sub"> · {r.branch}</span> : null}
            </button>
          ) : (
            <Dash />
          ),
      },
      {
        id: "rechnung",
        header: "Rechnung",
        sortable: true,
        sortField: "rechnungsnummer",
        width: 118,
        cell: (r) => <span>{r.invoiceNbr}</span>,
      },
      {
        id: "zeitraum",
        header: "Zeitraum",
        width: 150,
        cell: (r) =>
          r.periodFrom || r.periodTo ? (
            <span>
              {formatDate(r.periodFrom)} – {formatDate(r.periodTo)}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        id: "faelligkeit",
        header: "Fällig",
        sortable: true,
        sortField: "faelligkeit",
        width: 110,
        align: "right",
        cell: (r) => {
          if (!r.dueDate) return <Dash />;
          const ueberfaellig = r.amZug === "VN am Zug" && new Date(r.dueDate) < new Date();
          return <span className={ueberfaellig ? "txt-danger" : undefined}>{formatDate(r.dueDate)}</span>;
        },
      },
      {
        id: "beitrag",
        header: "Beitrag",
        sortable: true,
        sortField: "beitrag",
        width: 110,
        align: "right",
        cell: (r) => (r.amount == null ? <Dash /> : <>{formatEuro(r.amount)}</>),
      },
      {
        id: "offenKunde",
        header: "Offen Kunde",
        sortable: true,
        sortField: "offenKunde",
        width: 120,
        align: "right",
        cell: (r) =>
          (r.customerOpen ?? 0) > 0 ? (
            <strong className="txt-danger">{formatEuro(r.customerOpen)}</strong>
          ) : (
            <span className="txt-muted">{formatEuro(0)}</span>
          ),
      },
      {
        id: "kundeBezahlt",
        header: "✓ Kunde",
        width: 70,
        align: "center",
        cell: (r) => (
          <BuchungsSchalter
            checked={(r.customerOpen ?? 0) <= 0 && (r.customerTarget ?? 0) > 0}
            disabled={r.isCancelled || r.customerPaysInsurer}
            title="Zahlung des Kunden erhalten"
            onToggle={(next) => buchen(r, { customerPaid: next })}
          />
        ),
      },
      {
        id: "offenVr",
        header: "Offen Versicherer",
        sortable: true,
        sortField: "offenVersicherer",
        width: 140,
        align: "right",
        cell: (r) =>
          (r.insurerOpen ?? 0) > 0 ? (
            <strong className="txt-warn">{formatEuro(r.insurerOpen)}</strong>
          ) : (
            <span className="txt-muted">{formatEuro(0)}</span>
          ),
      },
      {
        id: "vrBezahlt",
        header: "✓ Versicherer",
        width: 92,
        align: "center",
        cell: (r) => (
          <BuchungsSchalter
            checked={(r.insurerOpen ?? 0) <= 0 && (r.insurerTarget ?? 0) > 0}
            disabled={r.isCancelled || r.customerPaysInsurer}
            title="An den Versicherer überwiesen"
            onToggle={(next) => buchen(r, { insurerPaid: next })}
          />
        ),
      },
      {
        id: "kav",
        header: "KaV",
        width: 60,
        align: "center",
        cell: (r) => (
          <BuchungsSchalter
            checked={r.customerPaysInsurer}
            // Sperre wie im Access-Original: kein KaV, wenn storniert oder
            // bereits an den Versicherer überwiesen.
            disabled={r.isCancelled || (r.insurerPaid ?? 0) > 0}
            title="Kunde zahlt direkt an den Versicherer"
            onToggle={(next) => buchen(r, { customerPaysInsurer: next })}
          />
        ),
      },
      {
        id: "storno",
        header: "Storno",
        width: 70,
        align: "center",
        cell: (r) => (
          <BuchungsSchalter
            checked={r.isCancelled}
            disabled={r.customerPaysInsurer || (r.insurerPaid ?? 0) > 0}
            title="Position stornieren"
            onToggle={(next) => buchen(r, { isCancelled: next })}
          />
        ),
      },
    ];
    return cols;
  }, [buchen, navigate]);

  const [sichtbar, setSichtbar] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (kompakt) setSichtbar({ kunde: !invoiceId, rechnung: !invoiceId });
  }, [kompakt]);

  return (
    <div className={kompakt ? "kvp-rd rd-posliste" : "kvp-rd kvp-rd-table-page"}>
      {!kompakt && (
        <div className="bar">
          <div className="search">
            {I.search}
            <input
              placeholder="Kunde, Rechnungs- oder Vertragsnummer …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="spacer" style={{ flex: 1 }} />
          <div className="rd-seg">
            <button
              className={nurOffen ? "active" : undefined}
              onClick={() => setNurOffen(true)}
            >
              Offene Posten
            </button>
            <button className={!nurOffen ? "active" : undefined} onClick={() => setNurOffen(false)}>
              Alle
            </button>
          </div>
          {total != null && <span className="rd-count">{total} Positionen</span>}
        </div>
      )}

      {fehler && <div className="rd-error">{fehler}</div>}

      <DataTable<PositionRow>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        sort={sort}
        onSortChange={setSort}
        columnVisibility={sichtbar}
        onColumnVisibilityChange={setSichtbar}
        onRowClick={(r) => navigate(`/rechnungen/${r.invoiceId}`)}
        rowClassName={(r) => (r.isCancelled ? "row-storniert" : undefined)}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        emptyText={nurOffen ? "Keine offenen Posten — alles ausgeglichen." : "Keine Rechnungspositionen."}
      />

      {/* Summenzeile: die DataTable hat keine Aggregation, deshalb hier.
          Die Werte kommen aus den X-Sum-Headern und gelten fuer die GESAMTE
          gefilterte Menge — nicht nur fuer die geladenen Zeilen. */}
      <div className="rd-summen">
        <span>
          Beitrag <strong>{formatEuro(summen.beitrag)}</strong>
        </span>
        <span>
          Offen Kunde <strong className="txt-danger">{formatEuro(summen.offenKunde)}</strong>
        </span>
        <span>
          Offen Versicherer <strong className="txt-warn">{formatEuro(summen.offenVersicherer)}</strong>
        </span>
      </div>
    </div>
  );
};

export default OffenePostenTabelle;
